import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { Briefcase } from 'lucide-react';

const ExperienceSection = styled.section`
  padding: 2rem 2rem 4rem;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background-color: ${({ theme }) => theme.background};
  position: relative;
  overflow: hidden;
`;

const ExperienceHeader = styled(motion.h2)`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.accent};
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 4px;
    background-color: ${({ theme }) => theme.accent};
    bottom: -10px;
    left: 0;
    animation: underline 1s infinite alternate;
  }

  @keyframes underline {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const DragScrollWrapper = styled.div`
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch; /* smooth scrolling on iOS */
`;

const ExperienceContainer = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  padding-bottom: 1rem;
  transition: transform 0.5s ease;
`;

const ExperienceCard = styled(motion.div)`
  flex: 0 0 320px;
  scroll-snap-align: start;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.cardBackground},
    ${({ theme }) => theme.background}
  );
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 1rem;
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
  transform: scale(${(props) => (props.active ? 1.1 : 1)});
  z-index: ${(props) => (props.active ? 10 : 1)};
  transition: transform 0.5s ease, box-shadow 0.5s ease;

  &:hover {
    transform: translateY(-12px) scale(1.05);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.25);
  }

  &::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -30%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.accent}33,
      transparent 60%
    );
    animation: pulse 6s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      opacity: 0.2;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.4;
    }
  }

  @media (max-width: 768px) {
    flex: 0 0 260px;
    padding: 1.5rem;
    transform: scale(${(props) => (props.active ? 1.07 : 1)});
  }

  @media (max-width: 480px) {
    flex: 0 0 220px;
    padding: 1.2rem;
    transform: scale(${(props) => (props.active ? 1.05 : 1)});
  }
`;

const JobTitle = styled(motion.h3)`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.accent};
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${({ theme }) => theme.accentHover};
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: rotate(15deg) scale(1.2);
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const CompanyName = styled.p`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.color};
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const Duration = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.color};
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const JobDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.color};
  line-height: 1.5;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const TechBadge = styled.span`
  display: inline-block;
  margin-top: 0.75rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.accent};
  border: 1px solid ${({ theme }) => theme.accent};
  border-radius: 0.5rem;
  margin-right: 0.5rem;
  background-color: ${({ theme }) => theme.accent}10;

  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 0.25rem 0.5rem;
  }
`;

const headerVariants = {
  hidden: { opacity: 0, y: -50 },
  visible: { opacity: 1, y: 0, transition: { duration: 1, ease: 'easeOut' } },
};

const Experience = () => {
  const experiences = [
    {
      title: 'Technical Cloud Support Intern',
      company: 'K21 Academy (Remote)',
      duration: 'February 2025 – Present',
      description:
        'Worked on cloud solutions using AWS, focusing on services like AWS SageMaker, Amazon S3, and Amazon Bedrock. Gained experience with AI and Machine Learning techniques, improving cloud-based support and automation.',
      tech: ['AWS', 'SageMaker', 'Amazon S3', 'Amazon Bedrock', 'AI/ML'],
    },
    {
      title: 'Software Developer Intern',
      company: 'Brightbrand (Remote)',
      duration: 'August 2024 – September 2024',
      description:
        'Developed scalable software features, improving platform performance by 15%. Streamlined the codebase to enhance maintainability and reduce deployment time by 20%.',
      tech: ['Node.js', 'Express', 'MongoDB'],
    },
    {
      title: 'Frontend Developer',
      company: 'IBM SkillsBuild (Remote)',
      duration: 'July 2024 – August 2024',
      description:
        'Led NeuroPeace, a team project addressing mental health awareness, leveraging React.js and Firebase to develop an interactive web platform.',
      tech: ['React.js', 'Firebase', 'Figma'],
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % experiences.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [experiences.length]);

  useEffect(() => {
    if (scrollRef.current) {
      // Calculate scroll based on current card width + gap for responsiveness
      let cardWidth = 320 + 24; // default card width + gap
      if (window.innerWidth <= 480) cardWidth = 220 + 24;
      else if (window.innerWidth <= 768) cardWidth = 260 + 24;

      scrollRef.current.scrollTo({ left: currentIndex * cardWidth, behavior: 'smooth' });
    }
  }, [currentIndex]);

  return (
    <ExperienceSection id="experience">
      <ExperienceHeader variants={headerVariants} initial="hidden" animate="visible">
        Experience
      </ExperienceHeader>

      <DragScrollWrapper ref={scrollRef}>
        <ExperienceContainer>
          {experiences.map((exp, idx) => (
            <ExperienceCard key={idx} active={idx === currentIndex}>
              <JobTitle>
                <Briefcase size={20} />
                {exp.title}
              </JobTitle>
              <CompanyName>{exp.company}</CompanyName>
              <Duration>{exp.duration}</Duration>
              <JobDescription>{exp.description}</JobDescription>
              <div>
                {exp.tech.map((tech, i) => (
                  <TechBadge key={i}>{tech}</TechBadge>
                ))}
              </div>
            </ExperienceCard>
          ))}
        </ExperienceContainer>
      </DragScrollWrapper>
    </ExperienceSection>
  );
};

export default Experience;
